'use client';

import { useMemo, useState } from 'react';
import {
  BodyStrong,
  Button,
  Input,
  LabelDefault,
  Spinner,
} from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { ArrowRight01Icon, Key01Icon, Search01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ApplicationCredential } from '../../../hooks/useApplicationCredentials';
import { navigateToApplicationSettingsSubTab } from '../../../utils/navigateToApplicationSettingsSubTab';
import { EmptyState } from '../../EmptyState';
import { CredentialsMenuCard } from './CredentialsMenuCard';
import {
  filterCredentialsBySearch,
  shouldShowCredentialsMenuSearch,
} from './filterCredentialsBySearch';

type CredentialsMenuContentProps = {
  applicationId?: string;
  credentials: ApplicationCredential[];
  isLoading: boolean;
  isMenuOpen: boolean;
  onClose: () => void;
};

export function CredentialsMenuContent({
  applicationId,
  credentials,
  isLoading,
  isMenuOpen,
  onClose,
}: CredentialsMenuContentProps) {
  const tMenu = useTranslations('layout.credentialsMenu');
  const tCommon = useTranslations('common');
  const [searchQuery, setSearchQuery] = useState('');

  const showSearch = shouldShowCredentialsMenuSearch(credentials.length);

  const filteredCredentials = useMemo(
    () =>
      filterCredentialsBySearch({
        credentials,
        query: showSearch ? searchQuery : '',
      }),
    [credentials, searchQuery, showSearch],
  );

  const handleManageInSettings = () => {
    navigateToApplicationSettingsSubTab('credentials');
    onClose();
  };

  const credentialCards = filteredCredentials.map((credential) => (
    <CredentialsMenuCard
      key={credential.id}
      applicationId={applicationId}
      credential={credential}
      isMenuOpen={isMenuOpen}
    />
  ));

  return (
    <VStack alignItems="stretch" gap={2} w="full" maxH="492px">
      <VStack alignItems="stretch" gap={2} flexShrink={0}>
        <BodyStrong color="fg.neutral.default" py={1} px={2}>
          {isLoading
            ? tMenu('triggerAriaLabel')
            : tMenu('title', { count: credentials.length })}
        </BodyStrong>
        {!isLoading && showSearch ? (
          <Box px={2}>
            <Box position="relative" w="full">
              <Search01Icon
                size={10}
                style={{
                  position: 'absolute',
                  left: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--colors-fg-muted)',
                  zIndex: 1,
                }}
              />
              <Input
                size="xs"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={tCommon('searchPlaceholder')}
                pl="6"
                h="6"
                w="full"
              />
            </Box>
          </Box>
        ) : null}
      </VStack>

      <Box
        flex={1}
        minH="160px"
        minW={0}
        w="full"
        overflowY={!isLoading && credentials.length > 0 ? 'auto' : undefined}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            h="full"
            minH="160px"
          >
            <Spinner size="md" />
          </Box>
        ) : credentials.length === 0 ? (
          <Box px={2} py={2}>
            <EmptyState
              icon={Key01Icon}
              title={tMenu('emptyTitle')}
              description={tMenu('emptyDescription')}
              iconSize={20}
            />
          </Box>
        ) : filteredCredentials.length === 0 ? (
          <Box px={2} py={2}>
            <EmptyState
              icon={Key01Icon}
              title={tMenu('noSearchResults')}
              iconSize={20}
            />
          </Box>
        ) : (
          <VStack alignItems="stretch" gap={2} px={2}>
            {credentialCards}
          </VStack>
        )}
      </Box>

      <Box px={2} pb={1} flexShrink={0}>
        <Button
          variant="ghost"
          size="sm"
          w="full"
          justifyContent="flex-start"
          cursor="pointer"
          onClick={handleManageInSettings}
        >
          <HStack gap={1} alignItems="center" cursor="pointer">
            <LabelDefault as="span" color="fg.neutral.muted" cursor="pointer">
              {tMenu('manageInSettings')}
            </LabelDefault>
            <ArrowRight01Icon
              size={16}
              color={token('colors.fg.neutral.muted')}
            />
          </HStack>
        </Button>
      </Box>
    </VStack>
  );
}
